import { useContext, createContext, useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import { getLocale } from "react-i18nify";

import { useSnackbar } from "notistack";
import { validFileExtensions } from "src/common/file";

import { getMasterData } from "src/api/master-data";
import { getSimilars } from "src/api/similar";
import {
  createProduct,
  getDataProductDetail,
  updateProduct,
  updatePendingProduct,
  createDraftProduct,
  updateDraftProduct,
} from "src/api/product";

import { Alert } from "@mui/material";

import ModalAlert from "src/components/modal/ModalAlert";
import ModalConfirm from "src/components/ui/ModalConfirm";
import Loading from "src/components/ui/Loading";
import { checkNotEmpty } from "src/common";
import { useLocation } from "react-router-dom";
import { useAuth } from "src/hooks/useAuth";

const UploadProductContext = createContext();

const WrapperAlert = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  height: 50px;
  z-index: 3000;
  animation: showAlert 0.5s;
  margin-top: 15px;
  display: flex;
  justify-content: center;
  @keyframes showAlert {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }
`;
const DivAlert = styled.div`
  min-width: 320px;
  width: fit-content;
  .MuiPaper-root-MuiAlert-root {
    border-radius: 16px;
  }
`;

const validationUploadSchema = yup.object().shape({
  user_id: yup.object().required(),
  brand: yup.object().required(),
  item_code: yup.string().required(),
  item_name: yup.string().required(),
  category: yup.object().required(),
  product_types: yup.array().of(yup.object()).min(1).max(5).required(),
  recommend_rooms: yup.array().of(yup.object()).min(1).required(),
  suitable_areas: yup.array().of(yup.object()).min(1).required(),
  product_features: yup.array().of(yup.object()).min(1).required(),
  similars: yup.array().of(yup.object()),
  bundles: yup.array().of(yup.object()),
  description: yup.string().nullable(),
  is_installer: yup.boolean(),
  is_choice: yup.boolean(),
  min_order_qty: yup
    .string()
    .matches(/^[1-9]\d*$/, "Number must not start with zero")
    .min(1, "Number must be at least 1")
    .required(),
});

const validationChoiceSchema = yup.object().shape({
  choices: yup.array().of(
    yup.object().shape({
      name: yup
        .string()
        .when("$is_choice", ([is_choice], schema) =>
          is_choice ? schema.required() : schema,
        ),
      group_image: yup.object({
        product_display_image: yup.mixed().test({
          name: "required",
          message: "product_display_image is a required field",
          test: (_, context) => context.originalValue !== "",
        }),
        product_full_image: yup.mixed().test({
          name: "required",
          message: "product_full_image is a required field",
          test: (_, context) => context.originalValue !== "",
        }),
        product_closeup_image: yup.mixed().nullable(),
        product_other1_image: yup.mixed().nullable(),
        product_other2_image: yup.mixed().nullable(),
        product_other3_image: yup.mixed().nullable(),
        product_video: yup.mixed().nullable(),
      }),
      group_price: yup.object({
        is_start_price: yup.boolean(),
        unit_price: yup.object().required(),
        price: yup
          .number()
          .nullable()
          .when("is_start_price", {
            is: false,
            then: (schema) => schema.min(1).required(),
          }),
        special_price: yup.number().nullable(),
        start_price: yup
          .number()
          .nullable()
          .when("is_start_price", {
            is: true,
            then: (schema) => schema.min(1).required(),
          }),
        sample_price: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.from[1]?.value?.is_sample &&
                context?.originalValue !== null &&
                context.originalValue < 1
              ) {
                return false;
              }
              return true;
            },
          }),
        install_price: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.options?.context?.is_installer &&
                context?.originalValue !== null &&
                context.originalValue < 0
              ) {
                return false;
              }
              return true;
            },
          }),
        price_per_sq: yup
          .number()
          .nullable(),
          // .when("unit_price", {
          //   is: (unit_price) =>
          //     unit_price?._id !== "63c90d859d945fef0df81f5f" && // ตร.ม.
          //     unit_price?._id !== "6480441071bbd9b8584b7008" && // อัน
          //     unit_price?._id !== "63c90d859d945fef0df81f62" && // หน่วย
          //     unit_price?._id !== "63c90d859d945fef0df81f60" && // ถัง
          //     unit_price?._id !== "63c90d859d945fef0df81f66" && // ชุด
          //     unit_price?._id !== "63c90d859d945fef0df81f64" && // ลิตร
          //     unit_price?._id !== "63c90d859d945fef0df81f63" && // แท่ง
          //     unit_price?._id !== "63c90d859d945fef0df81f6a" && // เมตร
          //     unit_price?._id !== "6480441071bbd9b8584b7007", // เส้น
          //   then: (schema) => schema.min(1).required(),
          // }),
      }),
      is_sample: yup.boolean(),
      color: yup.object().required(),
      texture_type: yup.object().required(),
      size: yup.object({
        quantity: yup.string().oneOf(["size", "capacity"]).required(),
        width: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                (context?.originalValue === null || context.originalValue <= 0)
              ) {
                return false;
              }
              return true;
            },
          }),
        height: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                (context?.originalValue === null || context.originalValue <= 0)
              ) {
                return false;
              }
              return true;
            },
          }),
        thickness: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                (context?.originalValue === null || context.originalValue <= 0)
              ) {
                return false;
              }
              return true;
            },
          }),
        unit_width: yup
          .object()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                context?.originalValue === null
              ) {
                return false;
              }
              return true;
            },
          }),
        unit_height: yup
          .object()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                context?.originalValue === null
              ) {
                return false;
              }
              return true;
            },
          }),
        unit_thickness: yup
          .object()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "size" &&
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f5f" && // ตร.ม.
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f62" && // หน่วย
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f63" && // แท่ง
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7008" && // อัน
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "6480441071bbd9b8584b7007" && // เส้น
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f6a" && // เมตร
                context?.originalValue === null
              ) {
                return false;
              }
              return true;
            },
          }),
        capacity_size: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "capacity" &&
                context?.from[1]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f64" && // ลิตร
                (context?.originalValue === null || context.originalValue <= 0)
              ) {
                return false;
              }
              return true;
            },
          }),
        unit_capacity: yup
          .object()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.parent.quantity === "capacity" &&
                context?.from[2]?.value?.group_price?.unit_price?._id !==
                  "63c90d859d945fef0df81f64" && // ลิตร
                context?.originalValue === null
              ) {
                return false;
              }
              return true;
            },
          }),
        sale_unit: yup
          .number()
          .nullable()
          .test({
            name: "requires",
            message: (value) => {
              return `${value.path} is a required field`;
            },
            test: (_, context) => {
              if (
                context?.from[1]?.value?.group_price?.unit_price?._id ===
                  "6461cfad3ddb1a01e34b09a4" && // กล่อง
                (context?.originalValue === null || context.originalValue < 1)
              ) {
                return false;
              }
              return true;
            },
          }),
        calculate_size: yup.number().nullable(),
        // .test({
        //   name: "requires",
        //   message: (value) => {
        //     return `${value.path} is a required field`
        //   },
        //   test: (_, context) => {
        //     if (
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f63" && // ตร.ม.
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "6480441071bbd9b8584b7008" && // อัน
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f62" && // หน่วย
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f60" && // ถัง
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f66" && // ชุด
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f64" && // ลิตร
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "63c90d859d945fef0df81f63" && // แท่ง
        //       context?.from[1]?.value?.group_price?.unit_price?._id !== "6480441071bbd9b8584b7007" // เส้น
        //     ) {
        //       return false;
        //     };
        //     return true;
        //   }
        // }),
      }),
      is_instock: yup.boolean(),
      stock_count: yup.number().nullable(),
      // .when("is_instock", {
      //   is: true,
      //   then: (schema) => schema.min(1).required(),
      // }),
      pre_order_date: yup.string().nullable(),
      hashtags: yup.array().of(yup.object()),
      pattern: yup.object().required(),
    }),
  ),
});

export const UploadContextProvider = ({ children }) => {
  const { user } = useAuth();
  let { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { enqueueSnackbar } = useSnackbar();

  /**
   * ! product
   */
  const defaultUpload = {
    _id: null,
    user_id: null,
    item_code: "",
    item_name: "",
    category: null,
    product_types: [],
    suitable_areas: [],
    similars: [],
    similar_admin: [],
    similar_seller: [],
    bundles: [],
    description: "",
    brand: null,
    is_installer: false,
    is_choice: false,
    recommend_rooms: [],
    product_features: [],
    min_order_qty: 1,
    similar_product_to_other_seller: [],
    similar_product_from_other_seller: [],
    product_tisi: null,
    product_certificates: [],
    has_certificate: false,
    has_tis: false,
    certificate_files: [],
  };
  const [dataUpload, setDataUpload] = useState(defaultUpload);

  const resolverUpload = useYupValidationResolver(validationUploadSchema);
  const {
    formState: {
      errors: uploadErrors,
      // isValid: isUploadValid,
    },
    // control: uploadControl,
    watch: watchUpload,
    trigger: triggerUpload,
    // getValues: getUploadValues,
    setValue: setUploadValue,
    reset: resetUpload,
  } = useForm({
    resolver: resolverUpload,
    defaultValues: defaultUpload,
  });

  // console.log("formState -> uploadErrors", uploadErrors);
  // console.log("formState -> dirtyFields", dirtyFields);

  /**
   * ! productChoice
   */
  const defaultUploadChoice = [
    {
      // _id: null,
      name: "",
      group_image: {
        product_display_image: "",
        product_full_image: "",
        product_closeup_image: "",
        product_other1_image: "",
        product_other2_image: "",
        product_other3_image: "",
        product_video: "",
      },
      group_price: {
        is_start_price: false,
        price: null,
        special_price: null,
        start_price: null,
        unit_price: null,
        sample_price: null,
        install_price: null,
        price_per_sq: null,
        // unit_price: {
        //   unit_en: null,
        //   unit_th: null,
        //   _id: null,
        // },
      },
      is_sample: false,
      // start_price: false,
      color: null,
      texture_type: null,
      size: {
        quantity: "size",
        width: null,
        height: null,
        thickness: null,
        unit_width: null,
        unit_height: null,
        unit_thickness: null,
        capacity_size: null,
        unit_capacity: null,
        calculate_size: null,
        sale_unit: null,
        // unit_capacity: {
        //   _id: null,
        //   unit_en: null,
        //   unit_th: null,
        // },
        // unit_height: {
        //   _id: null,
        //   unit_en: null,
        //   unit_th: null,
        // },
        // unit_thickness: {
        //   _id: null,
        //   unit_en: null,
        //   unit_th: null,
        // },
        // unit_width: {
        //   _id: null,
        //   unit_en: null,
        //   unit_th: null,
        // },
      },
      is_instock: false,
      stock_count: null,
      pre_order_date: null,
      hashtags: [],
      pattern: null,
    },
  ];
  const [dataUploadChoice, setDataUploadChoice] = useState(defaultUploadChoice);

  const resolverChoice = useYupValidationResolver(validationChoiceSchema, {
    is_choice: dataUpload?.is_choice,
    is_installer: dataUpload?.is_installer,
  });
  const {
    formState: {
      errors: choiceErrors,
      // isValid: isChoiceValid
    },
    control: choiceControl,
    watch: watchChoice,
    trigger: triggerChoice,
    getValues: getChoiceValues,
    setValue: setChoiceValue,
    reset: resetChoice,
  } = useForm({
    resolver: resolverChoice,
    defaultValues: { choices: [...defaultUploadChoice] },
  });

  const { append: appendChoice, remove: removeChoice } = useFieldArray({
    control: choiceControl,
    name: "choices",
  });

  // console.log("formState -> choiceErrors", choiceErrors);
  // console.log("formState -> isChoiceValid", isChoiceValid);

  /**
   * @other
   */

  const [dataSimilar, setDataSimilar] = useState({
    similar_admin: [],
    similar_seller: [],
    similar_product_to_other_seller: [],
    similar_product_from_other_seller: [],
  });
  const [isAlert, setIsAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const initialAlert = {
    visible: false,
    textError: "",
    typeError: "success",
  };
  const [alertInfo, setAlertInfo] = useState(initialAlert);
  const initialConfirm = {
    visible: false,
    title: "",
    confirmFunc: () => {},
  };
  const [confirmInfo, setConfirmInfo] = useState(initialConfirm);
  const [masterdata, setMasterData] = useState({
    brand_data: [],
    category_data: [],
    color_data: [],
    hashtag_data: [],
    pattern_data: [],
    productType_data: [],
    suitableArea_data: [{ _id: null }],
    textureType_data: [],
    unitArea_data: [],
    unitCapacityArea_data: [],
    unitPrice_data: [],
    seller_data: [],
    recommendRoom_data: [],
    productFeature_data: [],
  });
  const [similarData, setSimilarData] = useState({
    similarAllAdmin: [],
    similarAllSeller: [],
  });

  const formatKeyToFieldName = [
    {
      key: "brand",
      value: "แบรนด์",
    },
    {
      key: "item_code",
      value: "รหัสสินค้า",
    },
    {
      key: "item_name",
      value: "ชื่อสินค้า",
    },
    {
      key: "category",
      value: "หมวดหมู่สินค้า",
    },
    {
      key: "product_types",
      value: "ประเภทสินค้า",
    },
    {
      key: "recommend_rooms",
      value: "สถานที่ใช้งาน",
    },
    {
      key: "suitable_areas",
      value: "พื้นที่การใช้งาน",
    },
    {
      key: "product_features",
      value: "คุณสมบัติเฉพาะของสินค้า",
    },
    {
      key: "product_certificates",
      value: "ใบรับรองของสินค้า",
    },
    {
      key: "certificate_files",
      value: "เอกสารใบรับรองของสินค้า",
    },
    {
      key: "product_tisi",
      value: "เอกสารมอก. ของสินค้า",
    },
    {
      key: "min_order_qty",
      value: "จำนวนการสั่งซื้อขั้นต่ำ",
    },
    // choices
    {
      key: "name",
      value: "ชื่อตัวเลือกสินค้า",
    },
    {
      key: "product_display_image",
      value: "Product card image",
    },
    {
      key: "product_full_image",
      value: "Product full image",
    },
    {
      key: "unit_price",
      value: "หน่วยราคา",
    },
    {
      key: "price",
      value: "ราคา",
    },
    {
      key: "start_price",
      value: "ราคาเริ่มต้น",
    },
    {
      key: "sample_price",
      value: "ราคาสินค้าตัวอย่าง",
    },
    {
      key: "install_price",
      value: "ราคาติดตั้ง",
    },
    {
      key: "price_per_sq",
      value: "ราคาต่อ ตรม.",
    },
    {
      key: "color",
      value: "สี",
    },
    {
      key: "texture_type",
      value: "ผิวสัมผัส",
    },
    {
      key: "width",
      value: "ความกว้าง",
    },
    {
      key: "height",
      value: "ความสูง",
    },
    {
      key: "thickness",
      value: "ความหนา",
    },
    {
      key: "unit_width",
      value: "หน่วยความกว้าง",
    },
    {
      key: "unit_height",
      value: "หน่วยความสูง",
    },
    {
      key: "unit_thickness",
      value: "หน่วยความหนา",
    },
    {
      key: "capacity_size",
      value: "ปริมาตร",
    },
    {
      key: "unit_capacity",
      value: "หน่วยปริมาตร",
    },
    {
      key: "sale_unit",
      value: "ปริมาณต่อหน่วย",
    },
    {
      key: "pattern",
      value: "ลวดลาย",
    },
  ];

  // ! product useEffect
  /**
   * @notice after form value change, then set value to dataUpload state
   */
  useEffect(() => {
    const subscription = watchUpload((values, { name, type }) => {
      setDataUpload(values);
    });
    return () => subscription.unsubscribe();
  }, [watchUpload]);

  /**
   * @notice trigger validation when dataUpload updated
   */
  useEffect(() => {
    triggerUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload]);

  // ! productChoice useEffect
  /**
   * @notice after form value change, then set value to dataUploadChoice state
   */
  useEffect(() => {
    const subscription = watchChoice((values, { name, type }) => {
      setDataUploadChoice([...values.choices]);
    });
    return () => subscription.unsubscribe();
  }, [watchChoice]);

  /**
   * @notice trigger validation when dataUploadChoice updated
   */
  useEffect(() => {
    // console.log("------ dataUploadChoice -----", dataUploadChoice);
    triggerChoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUploadChoice]);

  useEffect(() => {
    const url = window.location.pathname;
    if (url.includes("/product/preview") || url.includes("/product/approve"))
      return;
    (async () => {
      const { data } = await getMasterData();
      setMasterData(data);
    })();
  }, []);

  useEffect(() => {
    isLoading
      ? (document.body.style.overflow = "hidden")
      : (document.body.style = "none");
  }, [isLoading]);

  useEffect(() => {
    const pathName = location.pathname;
    if (pathName.includes("/product/upload")) {
      const formatSimilarData = (data) => ({
        similarAllAdmin:
          data.similarAllAdmin?.map((item) => ({ similar: item })) || [],
        similarAllSeller:
          data.similarAllSeller?.map((item) => ({ similar: item })) || [],
      });

      const fetchSimilarProducts = async () => {
        try {
          const userId =
            user.role_id === 3 ? user._id : dataUpload?.user_id?.user?._id;
          const { data } = await getSimilars(userId, productId || null);

          if (!data.similarAllAdmin && !data.similarAllSeller) {
            setSimilarData({ similarAllAdmin: [], similarAllSeller: [] });
            return;
          }

          setSimilarData(formatSimilarData(data));
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, dataUpload?.user_id]);

  useEffect(() => {
    if (productId === undefined) return;
    const pathname = window.location.pathname;
    setIsLoading(true);
    (async () => {
      await getDataProductDetail(productId)
        .then(({ data }) => {
          if (data) {
            resetUpload({
              ...data.product,
              is_choice: data.product_choices.length > 1 ? true : false,
            });
            resetChoice({
              choices: [...data.product_choices],
            });
            if (pathname.includes("upload")) {
              // set default quantity condition for product draft from sale-order
              let formatChoice = data.product_choices.map((choice) => {
                let formatChoice;
                if (
                  choice?.group_price?.unit_price?._id ===
                    "63c90d859d945fef0df81f60" || // gallon
                  choice?.group_price?.unit_price?._id ===
                    "63c90d859d945fef0df81f66" // set
                ) {
                  formatChoice = {
                    ...choice,
                    size: {
                      ...choice?.size,
                      quantity: "capacity",
                    },
                  };
                } else if (
                  choice?.group_price?.unit_price?._id ===
                  "63c90d859d945fef0df81f64" // litre
                ) {
                  formatChoice = {
                    ...choice,
                    size: {
                      ...choice?.size,
                      capacity_size: 1,
                      unit_capacity: masterdata?.unitArea_data.find(
                        (el) => el._id === "63c90df59d945fef0df81f6d",
                      ), // litre.
                      quantity: "capacity",
                    },
                  };
                } else {
                  if (
                    choice?.group_price?.unit_price?._id ===
                    "63c90d859d945fef0df81f5f"
                  ) {
                    // sqm.
                    formatChoice = {
                      ...choice,
                      size: {
                        ...choice?.size,
                        calculate_size: 1,
                        width: 1,
                        height: 1,
                        unit_width: masterdata?.unitArea_data.find(
                          (el) => el._id === "63c90d359d945fef0df81f5a",
                        ), // m.
                        unit_height: masterdata?.unitArea_data.find(
                          (el) => el._id === "63c90d359d945fef0df81f5a",
                        ), // m.
                      },
                    };
                  } else if (
                    choice?.group_price?.unit_price?._id ===
                    "63c90d859d945fef0df81f67"
                  ) {
                    // sq.ft.
                    // dataUp?.size?.calculate_size = 0.0929;
                    formatChoice = {
                      ...choice,
                      size: {
                        ...choice?.size,
                        calculate_size: 0.0929,
                      },
                    };
                  } else if (
                    choice?.group_price?.unit_price?._id ===
                    "63c90d859d945fef0df81f69"
                  ) {
                    // yard
                    // dataUp?.size?.calculate_size = 0.836;
                    formatChoice = {
                      ...choice,
                      size: {
                        ...choice?.size,
                        calculate_size: 0.836,
                      },
                    };
                  } else {
                    formatChoice = {
                      ...choice,
                    };
                  }
                }
                return formatChoice;
              });
              resetUpload({
                ...data.product,
                // choice: JSON.parse(JSON.stringify(data.product_choices)),
                choice: formatChoice,
                is_choice: data.product_choices.length > 1 ? true : false,
              });
              resetChoice({
                choices: [...formatChoice],
              });
            }
            setIsLoading(false);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          navigate("/404");
        });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname.includes("upload") || pathname.includes("preview")) {
      if (productId === undefined && dataUpload?._id === null) {
        if (user.role_id === 3)
          resetUpload({
            ...defaultUpload,
            user_id: {
              seller: user?.seller,
              user: user?._id,
            },
          });
        else resetUpload(defaultUpload);
        resetChoice({
          choices: [...defaultUploadChoice],
        });
        setDataSimilar({
          similar_admin: [],
          similar_seller: [],
          similar_product_to_other_seller: [],
          similar__product_from_other_seller: [],
        });
      }
    } else {
      resetUpload(defaultUpload);
      resetChoice({
        choices: [...defaultUploadChoice],
      });
      setDataSimilar({
        similar_admin: [],
        similar_seller: [],
        similar_product_to_other_seller: [],
        similar_product_from_other_seller: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    if (
      dataUpload?.similars.length === 0 &&
      dataUpload?.similar_product_to_other_seller.length === 0 &&
      dataUpload?.similar_product_from_other_seller.length === 0 &&
      dataUpload?.bundles.length === 0
    )
      return;

    const hasSimilarProductToOtherSeller =
      dataUpload?.similar_product_to_other_seller?.length > 0;
    const hasSimilarProductFromOtherSeller =
      dataUpload?.similar_product_from_other_seller?.length > 0;

    setDataSimilar({
      similar_seller: dataUpload?.similars?.filter(
        ({ similar }) => similar.product?.user?._id === dataUpload?.user._id,
      ),
      similar_admin: dataUpload?.similars?.filter(
        ({ similar }) => similar.product?.user?._id !== dataUpload?.user._id,
      ),
      similar_product_to_other_seller: hasSimilarProductToOtherSeller
        ? dataUpload?.similar_product_to_other_seller
        : [],
      similar_product_from_other_seller: hasSimilarProductFromOtherSeller
        ? dataUpload?.similar_product_from_other_seller
        : [],
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataUpload?.similars,
    dataUpload?.similar_product_to_other_seller,
    dataUpload?.similar_product_from_other_seller,
  ]);

  const isAllowedMedia = (file) => {
    const allowedExtensions = ["png", "jpg", "jpeg"];

    if (!validFileExtensions(file, allowedExtensions)) {
      enqueueSnackbar(
        `File type is not allowed. Only accept ${allowedExtensions.join(
          ", ",
        )}.`,
        { variant: "error" },
      );
      return false;
    } else if (file.size > 2428800) {
      enqueueSnackbar(`File size is too large. Max file size is 2MB`, {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleUploadChange = async (event, replaceName, replaceValue) => {
    // console.log('---- e ---', event, '--- replaceValue ---', replaceValue)
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");

    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (key === "choices") {
      let [objIndex, objKey, subObjKey, subObjVal] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getChoiceValues(editingKey);

      // console.log('---- editingRow ---', editingRow, ', ==== objKey ====', objKey, ', ------ subObjKey -----', subObjKey, ',==== value ====', value);

      if (objKey === "group_image") {
        if (subObjKey === "product_video") {
          updated = {
            ...editingRow,
            group_image: {
              ...editingRow.group_image,
              product_video: value,
            },
          };
        } else {
          if (
            typeof value === "object" &&
            value !== null &&
            !isAllowedMedia(value)
          )
            return;

          updated = {
            ...editingRow,
            group_image: {
              ...editingRow.group_image,
              [subObjKey]: value,
            },
          };
        }
      } else if (objKey === "group_price") {
        if (subObjKey === "is_start_price") {
          if (value) {
            updated = {
              ...editingRow,
              group_price: {
                ...editingRow.group_price,
                is_start_price: true,
                start_price: +editingRow?.group_price?.price,
                price: null,
              },
            };
          } else {
            updated = {
              ...editingRow,
              group_price: {
                ...editingRow.group_price,
                is_start_price: false,
                start_price: null,
                price: +editingRow?.group_price?.start_price,
              },
            };
          }
        } else if (subObjKey === "unit_price") {
          if (
            value?._id === "63c90d859d945fef0df81f60" || // gallon
            value?._id === "63c90d859d945fef0df81f66" // set
          ) {
            updated = {
              ...editingRow,
              size: {
                ...editingRow?.size,
                quantity: "capacity",
                calculate_size: null,
                width: null,
                height: null,
                thickness: null,
                unit_width: null,
                unit_height: null,
                unit_thickness: null,
                sale_unit: null,
              },
              group_price: {
                ...editingRow?.group_price,
                unit_price: value,
                price_per_sq: null,
              },
            };
          } else if (value?._id === "63c90d859d945fef0df81f64") {
            // litre
            updated = {
              ...editingRow,
              size: {
                ...editingRow?.size,
                capacity_size: 1,
                unit_capacity: masterdata?.unitArea_data.find(
                  (el) => el._id === "63c90df59d945fef0df81f6d",
                ), // litre.
                quantity: "capacity",
                calculate_size: null,
                width: null,
                height: null,
                thickness: null,
                unit_width: null,
                unit_height: null,
                unit_thickness: null,
                sale_unit: null,
              },
              group_price: {
                ...editingRow?.group_price,
                unit_price: value,
                price_per_sq: null,
              },
            };
          } else {
            let unitUpdated;

            unitUpdated = {
              ...editingRow,
              size: {
                ...editingRow?.size,
                quantity: "size",
                capacity_size: null,
                unit_capacity: null,
                sale_unit: null,
              },
              group_price: {
                ...editingRow?.group_price,
                unit_price: value,
                price_per_sq: null,
              },
            };

            if (value?._id === "63c90d859d945fef0df81f5f") {
              // sqm.
              updated = {
                ...unitUpdated,
                size: {
                  ...unitUpdated?.size,
                  calculate_size: 1,
                  width: 1,
                  height: 1,
                  unit_width: masterdata?.unitArea_data.find(
                    (el) => el._id === "63c90d359d945fef0df81f5a",
                  ), // m.
                  unit_height: masterdata?.unitArea_data.find(
                    (el) => el._id === "63c90d359d945fef0df81f5a",
                  ), // m.
                },
              };
            } else if (value?._id === "63c90d859d945fef0df81f67") {
              // sq.ft.
              // dataUp?.size?.calculate_size = 0.0929;
              updated = {
                ...unitUpdated,
                size: {
                  ...unitUpdated?.size,
                  calculate_size: 0.0929,
                },
              };
            } else if (value?._id === "63c90d859d945fef0df81f69") {
              // yard
              // dataUp?.size?.calculate_size = 0.836;
              updated = {
                ...unitUpdated,
                size: {
                  ...unitUpdated?.size,
                  calculate_size: 0.836,
                },
              };
            } else {
              updated = {
                ...unitUpdated,
                size: {
                  ...unitUpdated?.size,
                  calculate_size: null,
                },
              };
            }
          }
        } else if (subObjKey === "sample_price") {
          if (subObjVal === "is_free") {
            if (value) {
              updated = {
                ...editingRow,
                group_price: {
                  ...editingRow?.group_price,
                  sample_price: null,
                },
              };
            } else {
              updated = {
                ...editingRow,
                group_price: {
                  ...editingRow?.group_price,
                  sample_price: "",
                },
              };
            }
          } else {
            updated = {
              ...editingRow,
              group_price: {
                ...editingRow?.group_price,
                sample_price: value,
              },
            };
          }
        } else if (subObjKey === "special_price") {
          if (value === "") {
            updated = {
              ...editingRow,
              group_price: {
                ...editingRow?.group_price,
                special_price: null,
              },
            };
          } else {
            updated = {
              ...editingRow,
              group_price: {
                ...editingRow?.group_price,
                special_price: value,
              },
            };
          }
        } else {
          updated = {
            ...editingRow,
            group_price: {
              ...editingRow.group_price,
              [subObjKey]: value,
            },
          };
        }
      } else if (objKey === "size") {
        // if (
        //   subObjKey === "quantity" ||
        //   subObjKey === "unit_capacity" ||
        //   subObjKey === "unit_width" ||
        //   subObjKey === "unit_height" ||
        //   subObjKey === "unit_thickness"
        // ) {
        //   updated = {
        //     ...editingRow,
        //     size: {
        //       ...editingRow.size,
        //       [subObjKey]: value,
        //     },
        //   };
        // } else {
        //   updated = {
        //     ...editingRow,
        //     size: {
        //       ...editingRow.size,
        //       [subObjKey]: +value,
        //     },
        //   };
        // }
        updated = {
          ...editingRow,
          size: {
            ...editingRow.size,
            [subObjKey]: value,
          },
        };
      } else if (objKey === "is_sample") {
        if (value) {
          updated = {
            ...editingRow,
            is_sample: value,
            group_price: {
              ...editingRow?.group_price,
              sample_price: "",
            },
          };
        } else {
          updated = {
            ...editingRow,
            is_sample: value,
            group_price: {
              ...editingRow?.group_price,
              sample_price: null,
            },
          };
        }
      } else if (objKey === "hashtags") {
        updated = {
          ...editingRow,
          hashtags: [
            ...value.map((props) => {
              if (typeof props === "string") {
                return { name: props };
              }
              return { name: props.name };
            }),
          ],
        };
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }

      setChoiceValue(editingKey, updated);
      return;
    } else if (key === "is_installer") {
      let [objKey] = rest;
      let updatedChoice;
      let editingChoice = getChoiceValues(`choices`);

      if (objKey === "is_free") {
        if (value) {
          // eslint-disable-next-line array-callback-return
          editingChoice.map((choice, index) => {
            updatedChoice = {
              ...choice,
              group_price: {
                ...choice.group_price,
                install_price: 0,
              },
            };

            setChoiceValue(`choices[${index}]`, updatedChoice);
          });
        } else {
          // eslint-disable-next-line array-callback-return
          editingChoice.map((choice, index) => {
            updatedChoice = {
              ...choice,
              group_price: {
                ...choice.group_price,
                install_price: "",
              },
            };

            setChoiceValue(`choices[${index}]`, updatedChoice);
          });
        }

        // setChoiceValue(editingKey, updatedChoice);
        return;
      } else if (objKey === "price") {
        // eslint-disable-next-line array-callback-return
        editingChoice.map((choice, index) => {
          updatedChoice = {
            ...choice,
            group_price: {
              ...choice.group_price,
              install_price: value,
            },
          };

          setChoiceValue(`choices[${index}]`, updatedChoice);
        });

        return;
      } else {
        if (value) {
          // eslint-disable-next-line array-callback-return
          editingChoice.map((choice, index) => {
            updatedChoice = {
              ...choice,
              group_price: {
                ...choice.group_price,
                install_price: "",
              },
            };

            setChoiceValue(`choices[${index}]`, updatedChoice);
          });
        } else {
          // eslint-disable-next-line array-callback-return
          editingChoice.map((choice, index) => {
            updatedChoice = {
              ...choice,
              group_price: {
                ...choice.group_price,
                install_price: null,
              },
            };

            setChoiceValue(`choices[${index}]`, updatedChoice);
          });
        }

        updated = value;
      }
    } else if (key === "min_order_qty" && value > 1000) {
      updated = 1000;
    } else {
      updated = value;
    }

    setUploadValue(editingKey, updated);
  };

  return (
    <UploadProductContext.Provider
      value={{
        dataUpload,
        masterdata,
        setDataUpload,
        dataUploadChoice,
        setDataUploadChoice,
        similarData,
        setIsAlert,
        dataSimilar,
        setDataSimilar,
        setIsLoading,
        onChangeUpload: handleUploadChange,
        uploadErrors,
        choiceErrors,
        onAppendChoice: () => {
          setUploadValue(`is_choice`, true);
          appendChoice(
            JSON.parse(
              JSON.stringify({
                ...dataUploadChoice[0],
                _id: null,
                name: "",
                group_image: {
                  product_display_image: "",
                  product_full_image: "",
                  product_closeup_image: "",
                  product_other1_image: "",
                  product_other2_image: "",
                  product_other3_image: "",
                  product_video: "",
                },
                is_instock: false,
                stock_count: null,
              }),
            ),
          );
        },
        onRemoveChoice: (index) => {
          if (dataUploadChoice.length < 3) {
            setUploadValue(`is_choice`, false);
            setChoiceValue(`choices.0.name`, "");
          }
          removeChoice(index);
        },
        formatKeyToFieldName,
        initialAlert,
        alertInfo,
        setAlertInfo,
        initialConfirm,
        confirmInfo,
        setConfirmInfo,
        // * new
        onCreateButton: async () => {
          setIsLoading(true);
          try {
            if (checkNotEmpty(dataUpload?._id) && productId !== undefined)
              return;
            setIsLoading(true);
            const datasimi = () => {
              const hasSimilarAdmin = dataSimilar.similar_admin.length > 0;
              const hasSimilarSeller = dataSimilar.similar_seller.length > 0;
              const hasSimilarToOtherSeller =
                dataSimilar.similar_product_to_other_seller.length > 0;

              if (hasSimilarAdmin && hasSimilarSeller) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [
                    ...dataSimilar.similar_admin,
                    ...dataSimilar.similar_seller,
                  ],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarAdmin) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [...dataSimilar.similar_admin],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarSeller) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [...dataSimilar.similar_seller],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              }
            };

            let save = {
              product: JSON.stringify(datasimi()),
              product_choices: JSON.stringify(dataUploadChoice),
              product_image: JSON.stringify(
                dataUploadChoice.map(({ group_image }) => group_image),
              ),
            };

            const arrayChoice = dataUploadChoice;
            arrayChoice.forEach((choice, index) => {
              // eslint-disable-next-line array-callback-return
              Object.keys(choice.group_image).map((key) => {
                save[`product_image[${index}][${key}]`] =
                  arrayChoice[index]["group_image"][key];
              });
            });

            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            try {
              await createProduct(data)
                .then(async (res) => {
                  setIsLoading(false);
                  enqueueSnackbar("Create Success!", {
                    variant: "success",
                  });
                  setTimeout(() => {
                    navigate(-1);
                    return "success";
                  }, 1500);
                })
                .catch((err) => {
                  setIsLoading(false);
                  enqueueSnackbar(err?.response?.data?.message, {
                    variant: "error",
                  });
                  return "error";
                });
            } catch (err) {
              setIsLoading(false);
              enqueueSnackbar(err?.response?.data?.message, {
                variant: "error",
              });
              return "error";
            }
          } catch (err) {
            setIsLoading(false);
            enqueueSnackbar(err?.response?.data?.message, {
              variant: "error",
            });
            return "error";
          }
        },
        onSaveEditButton: async () => {
          setIsLoading(true);
          try {
            if (!checkNotEmpty(dataUpload?._id)) return;
            setIsLoading(true);
            const datasimi = () => {
              const hasSimilarAdmin = dataSimilar.similar_admin.length > 0;
              const hasSimilarSeller = dataSimilar.similar_seller.length > 0;
              const hasSimilarToOtherSeller =
                dataSimilar.similar_product_to_other_seller.length > 0;
              if (hasSimilarAdmin && hasSimilarSeller) {
                return {
                  ...dataUpload,
                  similars: [
                    ...dataSimilar.similar_admin,
                    ...dataSimilar.similar_seller,
                  ],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarAdmin) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_admin],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarSeller) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_seller],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else {
                return {
                  ...dataUpload,
                  similars: [],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              }
            };
            let save = {
              product: JSON.stringify(datasimi()),
              product_choices: JSON.stringify(dataUploadChoice),
              product_image: JSON.stringify(
                dataUploadChoice.map(({ group_image }) => group_image),
              ),
            };
            const arrayChoice = dataUploadChoice;
            arrayChoice.forEach((choice, index) => {
              // eslint-disable-next-line array-callback-return
              Object.keys(choice.group_image).map((key) => {
                save[`product_image[${index}][${key}]`] =
                  arrayChoice[index]["group_image"][key];
              });
            });

            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            if (user?.role_id === 1) {
              await updateProduct(data)
                .then(async (res) => {
                  const { data } = await getDataProductDetail(dataUpload?._id);
                  resetUpload({
                    ...data.product,
                    is_choice: data.product_choices.length > 1 ? true : false,
                  });
                  resetChoice({
                    choices: [...data.product_choices],
                  });
                  setIsLoading(false);
                  enqueueSnackbar("Update product success!", {
                    variant: "success",
                  });
                  setTimeout(() => {
                    // setAlertInfo(initialAlert);
                    if (user.role_id === 1) navigate(-1);
                    // if (userData.role_id === 1) navigate(`/product/list`);
                    else navigate(`/product/upload/${res.data.product._id}`);
                    return "success";
                  }, 1500);
                })
                .catch((err) => {
                  setIsLoading(false);
                  enqueueSnackbar(err?.response?.data?.message, {
                    variant: "error",
                  });
                  return "error";
                });
            } else {
              await updatePendingProduct(data)
                .then(async (res) => {
                  const { data } = await getDataProductDetail(dataUpload?._id);
                  setDataUpload(data.product);
                  setDataUploadChoice(data.product_choices);
                  setIsLoading(false);
                  enqueueSnackbar("Update pending product success!", {
                    variant: "success",
                  });
                  // setAlertInfo({
                  //   ...alertInfo,
                  //   visible: true,
                  //   textError: "update product success",
                  //   typeError: "success",
                  // });
                  setTimeout(() => {
                    // setAlertInfo(initialAlert);
                    return "success";
                  }, 1500);
                })
                .catch((err) => {
                  setIsLoading(false);
                  enqueueSnackbar(err?.response?.data?.message, {
                    variant: "error",
                  });
                  // setAlertInfo({
                  //   ...alertInfo,
                  //   visible: true,
                  //   textError: err?.response?.data?.message,
                  //   typeError: "error",
                  // });
                  // setTimeout(() => {
                  //   setAlertInfo(initialAlert);
                  // }, 1500);
                  return "error";
                });
            }
          } catch (err) {
            await getDataProductDetail(productId)
              .then(({ data }) => {
                setIsLoading(false);
                setDataUpload(data.product);
                setDataUploadChoice(data.product_choices);
              })
              .catch((err) => {
                setIsLoading(false);
                navigate("/404");
              });
            enqueueSnackbar(err?.response?.data?.message, {
              variant: "error",
            });
            return "error";
          }
          return "success";
        },
        onSaveDraftButton: async () => {
          setIsLoading(true);
          try {
            // if (dataUpload._id !== null && productId !== undefined) return;
            const datasimi = () => {
              const hasSimilarAdmin = dataSimilar.similar_admin.length > 0;
              const hasSimilarSeller = dataSimilar.similar_seller.length > 0;
              const hasSimilarToOtherSeller =
                dataSimilar.similar_product_to_other_seller.length > 0;
              if (hasSimilarAdmin && hasSimilarSeller) {
                return {
                  ...dataUpload,
                  similars: [
                    ...dataSimilar.similar_admin,
                    ...dataSimilar.similar_seller,
                  ],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarAdmin) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_admin],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else if (hasSimilarSeller) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_seller],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              } else {
                return {
                  ...dataUpload,
                  similars: [],
                  similar_product_to_other_seller: hasSimilarToOtherSeller
                    ? [...dataSimilar.similar_product_to_other_seller]
                    : [],
                };
              }
            };
            let save = {
              product: JSON.stringify(datasimi()),
              product_choices: JSON.stringify(dataUploadChoice),
              product_image: JSON.stringify(
                dataUploadChoice.map(({ group_image }) => group_image),
              ),
            };
            const arrayChoice = dataUploadChoice;
            arrayChoice.forEach((choice, index) => {
              // eslint-disable-next-line array-callback-return
              Object.keys(choice.group_image).map((key) => {
                save[`product_image[${index}][${key}]`] =
                  arrayChoice[index]["group_image"][key];
              });
            });

            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            if (checkNotEmpty(dataUpload?._id)) {
              await updateDraftProduct(data)
                .then(async (res) => {
                  const { data } = await getDataProductDetail(dataUpload?._id);
                  resetUpload({
                    ...data.product,
                    is_choice: data.product_choices.length > 1 ? true : false,
                  });
                  resetChoice({
                    choices: [...data.product_choices],
                  });
                  setIsLoading(false);
                  enqueueSnackbar("Update draft Success!", {
                    variant: "success",
                  });
                  return "success";
                })
                .catch((err) => {
                  setIsLoading(false);
                  enqueueSnackbar(err?.response?.data?.message, {
                    variant: "error",
                  });
                  return "error";
                });
            } else {
              await createDraftProduct(data)
                .then(async (res) => {
                  setIsLoading(false);
                  enqueueSnackbar("Create draft success!", {
                    variant: "success",
                  });
                  setTimeout(() => {
                    navigate(`/product/upload/${res.data.product._id}`);
                    return "success";
                  }, 1500);
                })
                .catch((err) => {
                  setIsLoading(false);
                  enqueueSnackbar(err?.response?.data?.message, {
                    variant: "error",
                  });
                  return "error";
                });
            }
          } catch (err) {
            setIsLoading(false);
            enqueueSnackbar(err?.response?.data?.message, {
              variant: "error",
            });
            return "error";
          }
        },
        onCreateExProductButton: async () => {
          setIsLoading(true);
          try {
            let save = {
              product: JSON.stringify(dataUpload),
              product_choices: JSON.stringify(dataUploadChoice),
            };
            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            return await createDraftProduct(data)
              .then(async (res) => {
                setIsLoading(false);
                enqueueSnackbar("Create draft success!", {
                  variant: "success",
                });
                return res.data;
              })
              .catch((err) => {
                setIsLoading(false);
                enqueueSnackbar(err?.response?.data?.message, {
                  variant: "error",
                });
                return "error";
              });
          } catch (err) {
            setIsLoading(false);
            enqueueSnackbar(err?.response?.data?.message, {
              variant: "error",
            });
            return "error";
          }
        },
        // *
        // ! old
        onNextPostButton: async () => {
          setIsLoading(true);
          try {
            if (dataUpload._id !== null && productId !== undefined) return;
            const datasimi = () => {
              if (
                dataSimilar.similar_admin.length > 0 &&
                dataSimilar.similar_seller.length > 0
              ) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [
                    ...dataSimilar.similar_admin,
                    ...dataSimilar.similar_seller,
                  ],
                };
              } else if (
                dataSimilar.similar_admin.length > 0 &&
                dataSimilar.similar_seller.length === 0
              ) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [...dataSimilar.similar_admin],
                };
              } else if (
                dataSimilar.similar_admin.length === 0 &&
                dataSimilar.similar_seller.length > 0
              ) {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [...dataSimilar.similar_seller],
                };
              } else {
                return {
                  ...dataUpload,
                  _id: null,
                  similars: [],
                };
              }
            };
            let save = {
              product: JSON.stringify(datasimi()),
              product_choices: JSON.stringify(dataUploadChoice),
            };

            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            try {
              await createProduct(data)
                .then(async (res) => {
                  setAlertInfo({
                    ...alertInfo,
                    visible: true,
                    textError: "update progress success",
                    typeError: "success",
                  });
                  setTimeout(() => {
                    setAlertInfo(initialAlert);
                    // setIsVisible(false);
                    setIsLoading(false);
                    navigate(`/product/upload/${res.data.product._id}`);
                    return "success";
                  }, 1500);
                })
                .catch((err) => {
                  setAlertInfo({
                    ...alertInfo,
                    visible: true,
                    textError: err?.response?.data?.message,
                    typeError: "error",
                  });
                  setTimeout(() => {
                    setAlertInfo(initialAlert);
                    setIsLoading(false);
                  }, 1500);
                  return "error";
                });
            } catch (e) {
              setAlertInfo({
                ...alertInfo,
                visible: true,
                textError: e.response.data.message,
                typeError: "error",
              });
              setTimeout(() => {
                setAlertInfo(initialAlert);
                setIsLoading(false);
              }, 1500);
              return "error";
            }
          } catch (e) {
            setAlertInfo({
              ...alertInfo,
              visible: true,
              textError: e.response.data.message,
              typeError: "error",
            });
            // setTypeError("error");
            // setTextError(e.response.data.message);
            // setIsVisible(true);
            setTimeout(() => {
              setAlertInfo(initialAlert);
              setIsLoading(false);
              // setIsVisible(false);
            }, 2500);
            return "error";
          }
        },
        onNextPatchButton: async () => {
          setIsLoading(true);
          try {
            if (dataUpload._id === null) return;
            const datasimi = () => {
              if (
                dataSimilar.similar_admin.length > 0 &&
                dataSimilar.similar_seller.length > 0
              ) {
                return {
                  ...dataUpload,
                  similars: [
                    ...dataSimilar.similar_admin,
                    ...dataSimilar.similar_seller,
                  ],
                };
              } else if (
                dataSimilar.similar_admin.length > 0 &&
                dataSimilar.similar_seller.length === 0
              ) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_admin],
                };
              } else if (
                dataSimilar.similar_admin.length === 0 &&
                dataSimilar.similar_seller.length > 0
              ) {
                return {
                  ...dataUpload,
                  similars: [...dataSimilar.similar_seller],
                };
              } else {
                return {
                  ...dataUpload,
                  similars: [],
                };
              }
            };
            let save = {
              product: JSON.stringify(datasimi()),
              product_choices: JSON.stringify(dataUploadChoice),
              product_image: JSON.stringify(
                dataUploadChoice.map(({ group_image }) => group_image),
              ),
            };
            const arrayChoice = dataUploadChoice;
            arrayChoice.forEach((choice, index) => {
              // eslint-disable-next-line array-callback-return
              Object.keys(choice.group_image).map((key) => {
                save[`product_image[${index}][${key}]`] =
                  arrayChoice[index]["group_image"][key];
              });
            });

            let data = new FormData();
            Object.keys(save).map((key) => {
              data.append(key, save[key]);
              return data;
            });
            if (dataUpload.certificate_files.length > 0) {
              dataUpload.certificate_files.forEach((file, i) => {
                // ชื่อฟิลด์ขึ้นกับ backend: 'certificate_files' หรือ 'certificate_files[]'
                data.append("certificate_files", file);
              });
            }
            await updateProduct(data)
              .then(async (res) => {
                const { data } = await getDataProductDetail(dataUpload._id);
                setDataUpload(data.product);
                setDataUploadChoice(data.product_choices);
                setAlertInfo({
                  ...alertInfo,
                  visible: true,
                  textError: "update progress success",
                  typeError: "success",
                });
                // setTypeError("success");
                // setTextError("update progress success");
                // setIsVisible(true);
                setTimeout(() => {
                  setAlertInfo(initialAlert);
                  setIsLoading(false);
                  // setIsVisible(false);
                  return "success";
                }, 1500);
              })
              .catch((err) => {
                setAlertInfo({
                  ...alertInfo,
                  visible: true,
                  textError: err?.response?.data?.message,
                  typeError: "error",
                });
                // setTypeError("error");
                // setTextError(err?.response?.data?.message);
                // setIsVisible(true);
                setTimeout(() => {
                  setAlertInfo(initialAlert);
                  setIsLoading(false);
                  // setIsVisible(false);
                }, 1500);
                return "error";
              });
          } catch (e) {
            await getDataProductDetail(productId)
              .then(({ data }) => {
                setIsLoading(false);
                setDataUpload(data.product);
                setDataUploadChoice(data.product_choices);
              })
              .catch((err) => {
                setIsLoading(false);
                navigate("/404");
              });
            setAlertInfo({
              ...alertInfo,
              visible: true,
              textError: e.response.data.message,
              typeError: "error",
            });
            // setTypeError("error");
            // setTextError(e.response.data.message);
            // setIsVisible(true);
            setTimeout(() => {
              setAlertInfo(initialAlert);
              setIsLoading(false);
              // setIsVisible(false);
            }, 1500);
            return "error";
          }
          return "success";
        },
        onBackButton: async () => {
          // setIsLoading(true);
          const { data } = await getDataProductDetail(productId);
          setDataUpload(data.product);
          setDataUploadChoice(data.product_choices);
          // setTimeout(() => {
          //   setIsLoading(false);
          // }, 1000);
        },
        // !
        onCopyProduct: async (code) => {
          setIsLoading(true);
          const { data } = await getDataProductDetail(code);
          const {
            _id,
            createdAt,
            created_by,
            updatedAt,
            updated_by,
            status,
            status_request,
            is_edit,
            is_public,
            ...rest_product
          } = data.product;
          // setDataUpload({
          //   ...rest_product,
          //   item_code: null,
          //   item_name: null,
          // });
          // setDataUploadChoice(
          //   data.product_choices.map((data) => {
          //     const { _id, ...rest } = data;
          //     return { ...rest };
          //   })
          // );
          resetUpload({
            ...rest_product,
            item_code: null,
            item_name: null,
            is_choice: data.product_choices.length > 1 ? true : false,
          });
          resetChoice({
            choices: data.product_choices.map((data) => {
              const { _id, createdAt, updatedAt, is_deal, ...rest } = data;
              return { ...rest };
            }),
          });
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        },
      }}
    >
      {children}
      {alertInfo.visible && (
        <WrapperAlert>
          <DivAlert>
            <Alert
              className="MuiPaper-root-MuiAlert-root"
              severity={alertInfo.typeError}
            >
              {alertInfo.textError}
            </Alert>
          </DivAlert>
        </WrapperAlert>
      )}
      {confirmInfo.visible ? (
        <ModalConfirm
          title={confirmInfo.title}
          callBack={(value) =>
            value === false
              ? setConfirmInfo(initialConfirm)
              : confirmInfo?.confirmFunc()
          }
          textCancel={getLocale() === "th" ? "ยกเลิก" : "cancel"}
          textConfirm={getLocale() === "th" ? "ลบ" : "delete"}
        />
      ) : null}
      {isAlert && <ModalAlert close={(disp) => setIsAlert(disp)} />}
      {isLoading ? <Loading /> : null}
    </UploadProductContext.Provider>
  );
};

export const UseUpload = () => {
  return useContext(UploadProductContext);
};
